import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CandleType, YAxisType } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { useModalRegistration } from '@/store/uiStore';
import { buildStyles } from './chartStyles';
import { XIcon } from '@/components/icons';
import { Link } from 'react-router-dom';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { HLC_UP_COLOR, HLC_DOWN_COLOR } from '@/indicators/hlcBars';

interface Props {
  onClose: () => void;
}

const DEFAULT_UP_COLOR        = '#26a69a';
const DEFAULT_DOWN_COLOR      = '#ef5350';
const HLC_DEFAULT_UP_COLOR    = HLC_UP_COLOR;
const HLC_DEFAULT_DOWN_COLOR  = HLC_DOWN_COLOR;
const DEFAULT_NO_CHANGE_COLOR = '#9598a1';

export function ChartSettingsModal({ onClose }: Props) {
  useModalRegistration();

  const chart             = useChartStore((s) => s.chart);
  const theme             = useChartStore((s) => s.theme);
  const activeCandleType  = useChartStore((s) => s.activeCandleType);
  const compareSymbols    = useChartStore((s) => s.compareSymbols);
  const priceScaleType    = useChartStore((s) => s.priceScaleType);

  const upColor           = useChartStore((s) => s.candleUpColor);
  const downColor         = useChartStore((s) => s.candleDownColor);
  const setCandleUpColor   = useChartStore((s) => s.setCandleUpColor);
  const setCandleDownColor = useChartStore((s) => s.setCandleDownColor);

  const hlcUpColor        = useChartStore((s) => s.hlcUpColor);
  const hlcDownColor      = useChartStore((s) => s.hlcDownColor);
  const setHlcUpColor     = useChartStore((s) => s.setHlcUpColor);
  const setHlcDownColor   = useChartStore((s) => s.setHlcDownColor);

  const showHGrid         = useChartStore((s) => s.showHGrid);
  const showVGrid         = useChartStore((s) => s.showVGrid);
  const setShowHGrid      = useChartStore((s) => s.setShowHGrid);
  const setShowVGrid      = useChartStore((s) => s.setShowVGrid);

  const showPriceMarkHigh = useChartStore((s) => s.showPriceMarkHigh);
  const showPriceMarkLow  = useChartStore((s) => s.showPriceMarkLow);
  const showLastPriceLine = useChartStore((s) => s.showLastPriceLine);
  const setShowPriceMarkHigh = useChartStore((s) => s.setShowPriceMarkHigh);
  const setShowPriceMarkLow  = useChartStore((s) => s.setShowPriceMarkLow);
  const setShowLastPriceLine = useChartStore((s) => s.setShowLastPriceLine);

  const showCrosshair     = useChartStore((s) => s.showCrosshair);
  const setShowCrosshair  = useChartStore((s) => s.setShowCrosshair);

  const yAxisReverse      = useChartStore((s) => s.yAxisReverse);
  const setYAxisReverse   = useChartStore((s) => s.setYAxisReverse);
  const setPriceScaleType = useChartStore((s) => s.setPriceScaleType);

  const isHLC       = activeCandleType === 'hlc';
  const isArea      = activeCandleType === CandleType.Area;
  const isComparing = compareSymbols.length > 0;

  function applyColors(up: string, down: string) {
    chart?.setStyles({
      candle: { bar: { upColor: up, upBorderColor: up, upWickColor: up, downColor: down, downBorderColor: down, downWickColor: down } },
    });
  }

  function applyHlcColors(up: string, down: string) {
    chart?.setStyles({ indicator: { ohlc: { upColor: up, downColor: down, noChangeColor: DEFAULT_NO_CHANGE_COLOR } } });
  }

  function applyGrid(h: boolean, v: boolean) {
    chart?.setStyles({ grid: { horizontal: { show: h }, vertical: { show: v } } });
  }

  function applyPriceMarks(high: boolean, low: boolean, lastLine: boolean) {
    chart?.setStyles({
      candle: {
        priceMark: {
          show: high || low || lastLine,
          high: { show: high },
          low:  { show: low  },
          last: { show: lastLine, line: { show: lastLine } },
        },
      },
    });
  }

  function resetDefaults() {
    chart?.removeIndicator('candle_pane', 'HLC');
    const base = buildStyles(theme === 'dark');
    chart?.setStyles({
      ...base,
      candle: {
        ...base.candle,
        type: CandleType.CandleSolid,
        priceMark: { show: true, high: { show: true }, low: { show: true }, last: { show: true, line: { show: true } } },
      },
      crosshair: { ...(base.crosshair as object), show: true },
    });
    setCandleUpColor(DEFAULT_UP_COLOR);
    setCandleDownColor(DEFAULT_DOWN_COLOR);
    setHlcUpColor(HLC_DEFAULT_UP_COLOR);
    setHlcDownColor(HLC_DEFAULT_DOWN_COLOR);
    setShowHGrid(true);
    setShowVGrid(true);
    setShowPriceMarkHigh(true);
    setShowPriceMarkLow(true);
    setShowLastPriceLine(true);
    setShowCrosshair(true);
    setPriceScaleType('normal');
    setYAxisReverse(false);
    chart?.setStyles({ yAxis: { type: YAxisType.Normal, reverse: false } } as never);
    /* reset — save via template */
  }

  const yAxisTypeValue =
    priceScaleType === 'log'        ? YAxisType.Log
    : priceScaleType === 'percentage' ? YAxisType.Percentage
    : YAxisType.Normal;

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-drawer-backdrop bg-black/50" onPointerDown={onClose} />

      {/* Slide-in panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Chart settings"
        className="fixed right-0 top-0 bottom-0 z-dropdown w-72 bg-modal-bg border-l border-border flex flex-col shadow-2xl animate-panel-slide-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <span className="text-base font-semibold text-modal-text">Chart Settings</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
            aria-label="Close"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-6">

          {/* Candle / Bar colors — hidden in area mode */}
          {!isArea && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-widest text-modal-muted mb-3">
                {isHLC ? 'Bar Colors' : 'Candle Colors'}
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-modal-text">Up color</span>
                  {isHLC
                    ? <ColorPicker value={hlcUpColor}   onChange={(v) => { setHlcUpColor(v); applyHlcColors(v, hlcDownColor); }} />
                    : <ColorPicker value={upColor}      onChange={(v) => { setCandleUpColor(v); applyColors(v, downColor); }} />
                  }
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-modal-text">Down color</span>
                  {isHLC
                    ? <ColorPicker value={hlcDownColor} onChange={(v) => { setHlcDownColor(v); applyHlcColors(hlcUpColor, v); }} />
                    : <ColorPicker value={downColor}    onChange={(v) => { setCandleDownColor(v); applyColors(upColor, v); }} />
                  }
                </div>
              </div>
            </section>
          )}

          {/* Price marks */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-modal-muted mb-3">Price Marks</p>
            <div className="flex flex-col gap-3.5">
              <ToggleRow label="High mark"       checked={showPriceMarkHigh}  onChange={(v) => { setShowPriceMarkHigh(v);  applyPriceMarks(v, showPriceMarkLow, showLastPriceLine); }} />
              <ToggleRow label="Low mark"        checked={showPriceMarkLow}   onChange={(v) => { setShowPriceMarkLow(v);   applyPriceMarks(showPriceMarkHigh, v, showLastPriceLine); }} />
              <ToggleRow label="Last price line" checked={showLastPriceLine}  onChange={(v) => { setShowLastPriceLine(v);  applyPriceMarks(showPriceMarkHigh, showPriceMarkLow, v); }} />
            </div>
          </section>

          {/* Grid */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-modal-muted mb-3">Grid</p>
            <div className="flex flex-col gap-3.5">
              <ToggleRow label="Horizontal lines" checked={showHGrid} onChange={(v) => { setShowHGrid(v); applyGrid(v, showVGrid); }} />
              <ToggleRow label="Vertical lines"   checked={showVGrid} onChange={(v) => { setShowVGrid(v); applyGrid(showHGrid, v); }} />
            </div>
          </section>

          {/* Crosshair */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-modal-muted mb-3">Crosshair</p>
            <ToggleRow
              label="Show crosshair"
              checked={showCrosshair}
              onChange={(v) => { setShowCrosshair(v); chart?.setStyles({ crosshair: { show: v } }); }}
            />
          </section>

          {/* Price Axis */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-modal-muted mb-3">Price Axis</p>
            <div className="flex flex-col gap-3.5">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isComparing ? 'text-modal-muted' : 'text-modal-text'}`}>
                  Axis type{isComparing ? ' (% while comparing)' : ''}
                </span>
                <div className="flex rounded-lg overflow-hidden border border-border">
                  {([YAxisType.Normal, YAxisType.Log, YAxisType.Percentage] as const).map((t) => {
                    const labels: Record<string, string> = {
                      [YAxisType.Normal]: 'Normal',
                      [YAxisType.Log]: 'Log',
                      [YAxisType.Percentage]: '%',
                    };
                    const active = yAxisTypeValue === t && !isComparing;
                    return (
                      <button
                        key={t}
                        disabled={isComparing}
                        onClick={() => {
                          const nextType =
                            t === YAxisType.Log        ? 'log'
                            : t === YAxisType.Percentage ? 'percentage'
                            : 'normal';
                          setPriceScaleType(nextType);
                          chart?.setStyles({ yAxis: { type: t } } as never);
                          /* template persists scale type */
                        }}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          isComparing
                            ? 'text-modal-muted cursor-not-allowed'
                            : active
                              ? 'bg-accent text-white'
                              : 'text-modal-muted hover:text-modal-text hover:bg-modal-hover'
                        }`}
                      >
                        {labels[t]}
                      </button>
                    );
                  })}
                </div>
              </div>
              <ToggleRow
                label="Reverse coordinates"
                checked={yAxisReverse}
                onChange={(v) => { setYAxisReverse(v); chart?.setStyles({ yAxis: { reverse: v } } as never); }}
              />
            </div>
          </section>
        </div>

        {/* Footer — Reset */}
        <div className="px-5 py-4 border-t border-border shrink-0">
          <button
            onClick={resetDefaults}
            className="w-full py-2 rounded-xl text-sm text-modal-muted border border-border hover:border-red-400/50 hover:text-red-400 hover:bg-red-500/5 transition-colors"
          >
            Reset to defaults
          </button>
          {/* The chart terminal has no room for a footer on phones, so the site
              links live here (desktop shows them in the footer strip instead). */}
          <nav aria-label="About and legal" className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-modal-muted">
            <Link to="/about" onClick={onClose} className="hover:text-accent">About</Link>
            <Link to="/methodology" onClick={onClose} className="hover:text-accent">Methodology</Link>
            <Link to="/mcp" onClick={onClose} className="hover:text-accent">MCP</Link>
            <Link to="/terms" onClick={onClose} className="hover:text-accent">Terms</Link>
            <Link to="/privacy" onClick={onClose} className="hover:text-accent">Privacy</Link>
          </nav>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-modal-text">{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          checked ? 'bg-accent' : 'bg-modal-hover'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
